import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, User, Search, ChevronRight, LogOut, Phone, Clock, MapPin, Check, HelpCircle, Store, XCircle, Ticket, MessageSquare } from 'lucide-react';
import { EmptyState, EmptyCartIllustration, NoRestaurantsIllustration, SkeletonRestaurantCard, SkeletonOrderCard, ShimmerImage } from './EmptyStates.jsx';
import { SFNewDealLogo } from './Logo.jsx';
import BRAND_COLORS, { COLOR_USAGE, GRADIENTS } from './brandColors.js';
import { auth, isFirebaseConfigured } from './firebase.js';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';
import * as api from './lib/api.js';
import TRANSLATIONS from './translations.js';
import { useViewTransition } from './useViewTransition.js';
import { ConfirmModal, useConfirm } from './ConfirmModal.jsx';
import { OTPInput } from './OTPInput.jsx';

export default function FamilyMealApp() {
  const { view: currentView, navigateTo, direction: viewDirection } = useViewTransition('home');
  // Back-compat shim so the dozens of existing setCurrentView call sites
  // keep working without each having to decide forward/back at the call site.
  // Where direction matters (back buttons, drill-in CTAs) we use navigateTo
  // directly; everywhere else this defaults to a forward slide.
  const setCurrentView = useCallback((next) => navigateTo(next, 'forward'), [navigateTo]);
  const { confirm, confirmProps } = useConfirm();
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [cart, setCart] = useState([]);
  const [language, setLanguage] = useState('en');
  // Default true so the Home grid shows skeleton cards from the first paint —
  // even during the Families lookup that runs *before* loadRestaurantsFromAirtable
  // is called. Flipped to false only when the fetch (or its demo-data fallback)
  // completes, so users never see the "No Restaurants Available" empty state flash.
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('all');
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [cuisineFilterOpen, setCuisineFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const [orderConfirmation, setOrderConfirmation] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orders, setOrders] = useState([]);
  // null = never fetched (don't show empty state); true = fetch in flight; false = done.
  const [ordersLoading, setOrdersLoading] = useState(null);
  // false until the cart view has had time to settle after a navigation. Suppresses
  // a brief flash of the "cart empty" state on tab tap so the transition feels
  // consistent with the Orders tab.
  const [cartSettled, setCartSettled] = useState(false);

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true); // Start as true to check for existing session
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [authError, setAuthError] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  
  // Airtable access now goes through the `/api/*` proxy (see src/lib/api.js).
  // No token or base id in the client — the Lambda holds the Airtable PAT and
  // scopes every request to the Firebase-authenticated caller.

  // Restaurant list stale-while-revalidate cache. Keyed by the user's family
  // profile ID so test users / real families never see each other's snapshots,
  // and capped at 5 minutes so a phone left open overnight refetches before
  // surfacing stale meal-limit / In-Service state.
  const RESTAURANT_CACHE_KEY = 'familyMealRestaurants';
  const RESTAURANT_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
  const readRestaurantCache = (profileId) => {
    try {
      const raw = localStorage.getItem(RESTAURANT_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.profileId !== profileId) return null;
      if (Date.now() - parsed.timestamp > RESTAURANT_CACHE_MAX_AGE_MS) return null;
      return parsed.restaurants;
    } catch {
      return null;
    }
  };
  const writeRestaurantCache = (profileId, restaurants) => {
    try {
      localStorage.setItem(RESTAURANT_CACHE_KEY, JSON.stringify({
        profileId,
        timestamp: Date.now(),
        restaurants,
      }));
    } catch {
      // Quota exceeded or storage disabled — silently skip; next fetch still works.
    }
  };
  
  // User profile
  const [userProfile, setUserProfile] = useState({
    id: 'recDemoFamily123',
    familyCode: "DEM O1234",
    name: "Demo Family",
    phone: "+14155551234",
    vouchersRemaining: 5,
    renewalDate: (() => { const d = new Date(); d.setDate(d.getDate() + (7 - d.getDay())); return `Sunday, ${d.getMonth() + 1}/${d.getDate()}`; })(),
    language: "en",
    dietaryRestrictions: ["No Restrictions"],
    zipcode: "94110" // Demo zip code (Mission district)
  });

  // Memoized style objects to prevent recreation on every render
  const tealBgStyle = useMemo(() => ({ backgroundColor: BRAND_COLORS.teal }), []);
  // tealColorStyle resolves to TURQUOISE (#2B75A1), not light teal (#49D2D0):
  // light teal as text on white is only 1.82:1 (fails WCAG hard); turquoise is
  // 5.05:1 (passes AA). Name kept for backward-compat so every call site
  // resolves to the AA-passing variant without a global sweep.
  const tealColorStyle = useMemo(() => ({ color: BRAND_COLORS.turquoise }), []);
  const tealBorderStyle = useMemo(() => ({ borderColor: BRAND_COLORS.teal }), []);
  // White on light teal fails WCAG (1.82:1); midnight on teal is 6.33:1 AA.
  // Pair tealBgStyle with onTealTextStyle on every primary CTA button.
  const onTealTextStyle = useMemo(() => ({ color: BRAND_COLORS.midnight }), []);

  // Directional arrow that flips for RTL languages. Unicode arrows aren't
  // bidi-mirrored automatically, so a literal "←" in an Arabic UI would
  // visually point away from the start side. Use {backArrow} instead.
  const backArrow = language === 'ar' ? '→' : '←';

  // Sync <html dir> and <html lang> with the active language. Setting it on
  // the document root (not just our app-root div) lets modals, portals, and
  // any future detached UI inherit the correct text direction without a
  // separate prop. Arabic flips to RTL; everything else stays LTR.
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthState();

    // Note: reCAPTCHA is created lazily inside sendVerificationCode rather than at mount.
    // Mount-time setup is fragile because the recaptcha-container DOM node may not exist
    // yet (authLoading=true on first render) and Firebase verifiers also break if the
    // underlying DOM node gets re-rendered before .verify() runs.
  }, []);

  // Reset the cart-settled flag each time we enter the Cart view. After a short
  // window we mark it settled, which is when the "Your cart is empty" state is
  // allowed to render. Duration mirrors the typical Airtable fetch in the Orders
  // tab (~300ms) so the two transitions feel consistent.
  useEffect(() => {
    if (currentView !== 'cart') return;
    setCartSettled(false);
    const t = setTimeout(() => setCartSettled(true), 300);
    return () => clearTimeout(t);
  }, [currentView]);

  // Start each view at the top. Without this, drilling into a restaurant kept the
  // window's prior scroll position, so the menu opened scrolled to the middle.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentView, selectedRestaurant?.id]);

  const checkAuthState = () => {
    // Check Firebase auth state if configured
    if (isFirebaseConfigured() && auth) {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          console.log('[Auth] User is signed in:', user.phoneNumber);
          setIsAuthenticated(true);
          // Load or create user profile
          loadUserProfile(user);
        } else {
          console.log('[Auth] No user signed in');
          setIsAuthenticated(false);
        }
        setAuthLoading(false);
      });
      return unsubscribe;
    } else {
      // Fallback to localStorage check for demo mode
      const savedAuth = localStorage.getItem('familyMealAuth');
      const savedProfile = localStorage.getItem('familyMealProfile');

      if (savedAuth === 'true' && savedProfile) {
        setIsAuthenticated(true);
        setUserProfile(JSON.parse(savedProfile));
        loadDemoData();
        // Load orders for saved session
        setTimeout(() => loadOrders(), 500);
      }
      setAuthLoading(false);
    }
  };

  const loadUserProfile = async (user) => {
    // Flip loading on at the start of the family lookup so the Home skeletons
    // show through both Airtable round-trips (Families + Restaurants). Without
    // this, a sign-in after a previous session would render briefly with the
    // stale loading=false from the prior fetch — long enough for the
    // "No Restaurants Available" empty state to flash before the skeleton.
    setLoading(true);
    try {
      // Resolve the caller's Families record through the proxy. The server derives
      // identity from the verified Firebase token (no client-supplied phone) and
      // returns the matched record, or 404 NO_FAMILY when there's no match.
      let family;
      try {
        family = await api.getFamily();
      } catch (e) {
        if (e.code === 'NO_FAMILY' || e.status === 404) family = null;
        else throw e;
      }

      if (family) {
        const familyRecord = family.fields;
        console.log('[Auth] Found family profile:', familyRecord['Family ID']);

        // Use the readable "CBO Text" lookup field — "CBOs" is a linked-record array of
        // recXXX IDs which can never match the human-readable name we compare against.
        const cboTextRaw = familyRecord['CBO Text'];
        const cbo = Array.isArray(cboTextRaw) ? cboTextRaw[0] : (cboTextRaw || null);

        const profile = {
          id: family.id,
          familyCode: familyRecord['Family ID'] || "FM" + user.uid.substring(0, 6).toUpperCase(),
          name: familyRecord["Child's Full Name"] || familyRecord["Guardian's Full Name"] || "Family User",
          phone: user.phoneNumber,
          vouchersRemaining: familyRecord['Vouchers Remaining'] || 0,
          renewalDate: familyRecord['Friendly Renewal Date'] || (familyRecord['Renewal Date'] ? new Date(familyRecord['Renewal Date']).toLocaleDateString('en-US', { weekday: 'long', month: 'numeric', day: 'numeric' }) : null),
          language: (() => {
            // Airtable stores full language names ("English", "Spanish", "中文", etc.);
            // TRANSLATIONS is keyed by ISO codes. Map known names → codes; fall back to current.
            const raw = (familyRecord['Language'] || '').toString().toLowerCase().trim();
            const map = {
              english: 'en', en: 'en',
              spanish: 'es', español: 'es', espanol: 'es', es: 'es',
              chinese: 'zh', '中文': 'zh', mandarin: 'zh', cantonese: 'zh', zh: 'zh',
              vietnamese: 'vi', 'tiếng việt': 'vi', 'tieng viet': 'vi', vi: 'vi',
              arabic: 'ar', 'العربية': 'ar', ar: 'ar'
            };
            return map[raw] || language || 'en';
          })(),
          dietaryRestrictions: familyRecord['Dietary Restriction'] || [],
          neighborhood: familyRecord['Neighborhood Choice'] || null,
          zipcode: familyRecord['Zip Code'] || null,
          cbo: cbo, // Community Business Organization - "SF New Deal" indicates staff/testing
          isTestUser: typeof cbo === 'string' && (cbo.toLowerCase().includes('sf new deal') || cbo.toLowerCase().includes('sfnd qc'))
        };

        setUserProfile(profile);
        localStorage.setItem('familyMealProfile', JSON.stringify(profile));
        setLanguage(profile.language); // Update app language to match user preference
        loadRestaurantsFromAirtable(profile.isTestUser, profile.id);
        // Load orders after profile is set
        setTimeout(() => loadOrders(), 500);
      } else {
        console.warn('[Auth] No family profile found for phone:', user.phoneNumber);
        // In production, surface a real error instead of silently loading dummy data.
        if (import.meta.env.PROD) {
          setAuthError(`No family record found for ${user.phoneNumber}. Please confirm your number matches the Airtable Families record, then try again.`);
          // Sign out so the user can retry with a different number
          try { await signOut(auth); } catch (e) { /* ignore */ }
          return;
        }
        // Dev-only: create a placeholder profile + load demo restaurants for local work
        const profile = {
          id: user.uid,
          familyCode: "FM" + user.uid.substring(0, 6).toUpperCase(),
          name: "New Family",
          phone: user.phoneNumber,
          vouchersRemaining: 0,
          language: language || "en",
          dietaryRestrictions: []
        };
        setUserProfile(profile);
        localStorage.setItem('familyMealProfile', JSON.stringify(profile));
        loadDemoData();
      }
    } catch (error) {
      console.error('[Auth] Error loading user profile:', error);
      if (import.meta.env.PROD) {
        setAuthError(`Could not load your family record: ${error.message}. Please reload and try again.`);
        try { await signOut(auth); } catch (e) { /* ignore */ }
        return;
      }
      // Dev-only fallback
      const profile = {
        id: user.uid,
        familyCode: "FM" + user.uid.substring(0, 6).toUpperCase(),
        name: "Family User",
        phone: user.phoneNumber,
        vouchersRemaining: 3,
        language: language || "en",
        dietaryRestrictions: []
      };
      setUserProfile(profile);
      localStorage.setItem('familyMealProfile', JSON.stringify(profile));
      loadDemoData();
    }
  };

  // Demo login
  // Load orders for current family
  const loadOrders = async () => {
    try {
      if (!userProfile?.id) {
        console.log('[Orders] No user profile, skipping order fetch');
        return;
      }

      setOrdersLoading(true);
      console.log('[Orders] Fetching orders for family:', userProfile.id);

      // Fetch the caller's active orders through the proxy. The server scopes to
      // this family (Family Record ID) and excludes cancelled rows; the client
      // keeps the language-aware join against the loaded restaurant/menu data.
      const data = await api.getOrders();

      if (data.records) {
        // Resolve restaurant record IDs → restaurant info (name, address, phone) from the
        // already-loaded restaurants list. Falls back to empty string if not found.
        const restaurantsById = Object.fromEntries(restaurants.map(r => [r.id, r]));

        const transformedOrders = data.records.map(record => {
          const fields = record.fields;
          const restaurantId = fields['Restaurants']?.[0];
          const restaurant = restaurantsById[restaurantId];

          // Build menu items list from the per-item amount. Resolve the item NAME
          // from the restaurant's loaded menu by position (id 1..6) so it's
          // language-aware and covers items #5/#6 — the Order Log only has
          // `Menu Item 1`..`Menu Item 4` lookup fields (5 and 6 don't exist), so
          // relying on those alone showed a literal "Menu Item 6" placeholder.
          const menuItems = [];
          for (let i = 1; i <= 6; i++) {
            const amount = fields[`Menu Item #${i} Amount`] || 0;
            if (amount > 0) {
              const menuMatch = restaurant?.menu?.find(m => m.id === i);
              const text = menuMatch
                ? getMenuItemName(menuMatch)
                : ((fields[`Menu Item ${i}`] || [])[0] || `Menu Item ${i}`);
              menuItems.push(`${amount}× ${text}`);
            }
          }

          return {
            id: record.id,
            restaurant: restaurant || null,
            restaurantName: restaurant?.dbaName || 'Restaurant',
            restaurantAddress: restaurant?.address || '',
            restaurantPhone: restaurant?.phone || '',
            status: fields['Status'] || 'Pending',
            createdTime: fields['Timestamp'] ? new Date(fields['Timestamp']) : new Date(),
            menuItems,
            vouchersUsed: fields['Total Meals'] || 0,
            familyCode: userProfile.familyCode,
            pickupInstructions: '',
            orderNumber: fields['ID'] ? String(fields['ID']) : record.id.slice(-6).toUpperCase()
          };
        });

        setOrders(transformedOrders);
        console.log('[Orders] Loaded', transformedOrders.length, 'active orders');
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('[Orders] Error loading orders:', error);
      if (import.meta.env.PROD) {
        // Fail loud in production — no dummy-order fallback
        setOrders([]);
        return;
      }
      // Dev-only demo fallback
      setOrders([
        {
          id: 'demo-order-1',
          restaurantName: 'Golden Dragon',
          restaurantAddress: '123 Grant Ave, San Francisco, CA 94108',
          restaurantPhone: '(415) 555-0123',
          status: 'Confirmed',
          createdTime: new Date(Date.now() - 30 * 60000), // 30 minutes ago
          menuItems: ['Orange Chicken Family Pack'],
          vouchersUsed: 1,
          familyCode: userProfile?.familyCode || 'DEMO1234',
          pickupInstructions: 'Call when you arrive',
          orderNumber: 'ORD001'
        }
      ]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    // Confirm before logging out so a family doesn't accidentally tap it and have
    // to go through SMS verification again.
    const ok = await confirm({
      title: t.logout,
      message: t.logoutConfirm,
      confirmLabel: t.logout,
      cancelLabel: t.cancel,
      variant: 'danger',
    });
    if (!ok) return;
    if (isFirebaseConfigured() && auth) {
      try {
        await signOut(auth);
        console.log('[Auth] User signed out');
      } catch (error) {
        console.error('[Auth] Sign out error:', error);
      }
    }
    setIsAuthenticated(false);
    setPhoneNumber('');
    setVerificationCode('');
    setVerificationId(null);
    setConfirmationResult(null);
    setCart([]);
    setRestaurants([]);
    setDemoMode(false);
    localStorage.removeItem('familyMealAuth');
    localStorage.removeItem('familyMealProfile');
    localStorage.removeItem(RESTAURANT_CACHE_KEY);
  };

  // Format phone number
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  // Validate + normalize a US phone number to E.164. Returns null for invalid input
  // (wrong digit count, or area code starting with 0/1 — what happens when a stray
  // "1" gets prefixed). Validating before reCAPTCHA prevents a bad number from
  // leaving the widget in a stuck state.
  const toE164US = (raw) => {
    const d = (raw || '').replace(/\D/g, '');
    let ten;
    if (d.length === 10) ten = d;
    else if (d.length === 11 && d[0] === '1') ten = d.slice(1);
    else return null;
    if (ten[0] === '0' || ten[0] === '1') return null;
    return `+1${ten}`;
  };

  // Fully tear down any rendered reCAPTCHA so the next attempt starts clean — the
  // verifier is single-use and otherwise persists after a failed send, breaking
  // every retry until the app is reloaded.
  const resetRecaptcha = () => {
    try { if (recaptchaVerifier) recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
    setRecaptchaVerifier(null);
    const el = document.getElementById('recaptcha-container');
    if (el) el.innerHTML = '';
  };

  const friendlySmsError = (err) => {
    const code = (err && (err.code || err.message)) || '';
    if (code.includes('invalid-phone-number')) return t.errInvalidNumber;
    if (code.includes('too-many-requests')) return t.errTooManyAttempts;
    if (code.includes('captcha') || code.includes('app-credential')) return t.errVerifyCouldNotStart;
    if (code.includes('quota')) return t.errSmsQuota;
    return t.errSendCodeGeneric;
  };

  // Send SMS verification code
  // Uses sendingCode (not authLoading) so the top-level loading-screen branch doesn't
  // unmount the login screen mid-send and tear down the recaptcha-container DOM node.
  const sendVerificationCode = async () => {
    setSendingCode(true);
    setAuthError('');

    try {
      // Validate BEFORE touching reCAPTCHA so a bad number can't corrupt it.
      const fullNumber = toE164US(phoneNumber);
      if (!fullNumber) {
        setAuthError(t.errInvalidNumber);
        setSendingCode(false);
        return;
      }

      console.log('[Auth] Sending verification code to:', fullNumber);

      // Use Firebase if configured, otherwise use demo mode
      if (isFirebaseConfigured() && auth) {
        // Start from a clean slate every time.
        resetRecaptcha();
        let verifier;
        try {
          verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => console.log('[Auth] reCAPTCHA solved'),
            'expired-callback': () => console.log('[Auth] reCAPTCHA expired')
          });
          setRecaptchaVerifier(verifier);
        } catch (err) {
          console.error('[Auth] reCAPTCHA init error:', err);
          resetRecaptcha();
          setAuthError(t.errVerifyCouldNotStart);
          setSendingCode(false);
          return;
        }
        try {
          const confirmation = await signInWithPhoneNumber(auth, fullNumber, verifier);
          setConfirmationResult(confirmation);
          setVerificationId('firebase-verification');
          setSendingCode(false);
          console.log('[Auth] Verification code sent via Firebase');
        } catch (firebaseError) {
          console.error('[Auth] Firebase error:', firebaseError);
          // Reset so the user can retry immediately without reloading.
          resetRecaptcha();
          setAuthError(friendlySmsError(firebaseError));
          setSendingCode(false);
        }
      } else {
        // Production builds must never silently fall back to demo SMS — surface a hard error so we notice missing Firebase config immediately.
        if (import.meta.env.PROD) {
          console.error('[Auth] Firebase is not configured in this production build. Check FIREBASE_* secrets in the deploy workflow.');
          setAuthError(t.errSmsNotConfigured);
          setSendingCode(false);
          return;
        }
        // Dev-only demo SMS fallback (when running locally without Firebase env vars)
        console.log('[Auth] Using demo mode (Firebase not configured) — DEV ONLY');
        setTimeout(() => {
          setVerificationId('demo-verification-id');
          setSendingCode(false);
          alert('Verification code sent! For demo, use any 6-digit code.');
        }, 1000);
      }

    } catch (error) {
      console.error('[Auth] Error sending code:', error);
      setAuthError(t.errSendCodeFailed);
      setSendingCode(false);
    }
  };

  // Verify code
  // Uses verifyingCode (not authLoading) for the same reason as sendVerificationCode —
  // keeps the verify screen mounted while Firebase confirms the code.
  const verifyCode = async () => {
    setVerifyingCode(true);
    setAuthError('');

    try {
      if (verificationCode.length !== 6) {
        setAuthError(t.errEnter6Digit);
        setVerifyingCode(false);
        return;
      }

      console.log('[Auth] Verifying code:', verificationCode);

      // Use Firebase if configured, otherwise use demo mode
      if (isFirebaseConfigured() && confirmationResult) {
        try {
          const result = await confirmationResult.confirm(verificationCode);
          console.log('[Auth] User signed in successfully:', result.user.phoneNumber);
          // Firebase onAuthStateChanged will handle setting isAuthenticated
          setVerifyingCode(false);
          localStorage.setItem('familyMealAuth', 'true');
        } catch (firebaseError) {
          console.error('[Auth] Verification error:', firebaseError);
          setAuthError(t.invalidCode);
          setVerifyingCode(false);
        }
      } else {
        // Production builds must never silently authenticate without Firebase
        if (import.meta.env.PROD) {
          console.error('[Auth] Firebase is not configured in this production build.');
          setAuthError(t.errAuthUnavailable);
          setVerifyingCode(false);
          return;
        }
        // Dev-only demo verify fallback
        console.log('[Auth] Using demo mode verification — DEV ONLY');
        setTimeout(() => {
          setIsAuthenticated(true);
          setVerifyingCode(false);
          localStorage.setItem('familyMealAuth', 'true');
          localStorage.setItem('familyMealProfile', JSON.stringify(userProfile));
          loadDemoData();
        }, 1000);
      }

    } catch (error) {
      console.error('[Auth] Error verifying code:', error);
      setAuthError(t.invalidCode);
      setVerifyingCode(false);
    }
  };

  const loadRestaurantsFromAirtable = async (isTestUser = false, profileId = null) => {
    // Stale-while-revalidate: if we have a cached restaurant list for this
    // profile, paint it instantly and skip the loading skeleton. The fresh
    // fetch below still runs in the background and replaces the data when
    // it arrives. On a Slow 3G connection this turns a multi-second blank
    // wait into an instant render.
    const cached = profileId ? readRestaurantCache(profileId) : null;
    if (cached) {
      setRestaurants(cached);
      setLoading(false);
      setHasAnimated(true);
    } else {
      setLoading(true);
    }

    try {
      setError(null);

      // Fetch Restaurants and both Hours tables. Menu items now live directly on
      // the Restaurants record (Menu Item #1..#4 per language) so Menu Submissions are
      // no longer fetched. Resource: 2nd Hours holds split-shift restaurants
      // (e.g. lunch + dinner). Big Apple Pizza closes after midnight; its close
      // time can be ≥ 24:00 in the Airtable raw integer.
      //
      // Fetch program data (Restaurants + both Hours tables) through the proxy.
      // No per-record filtering server-side — the client-side filter below handles
      // test users, Test=true rows, and the Matt's Bistro fallback. The menu/hours
      // building below is unchanged; it just reads from the proxy's record arrays.
      const programData = await api.getRestaurants();
      const restaurantsData = { records: programData.restaurants };
      const hoursData = { records: programData.hours };
      const secondHoursData = { records: programData.hours2 };

      // Helper function to get attachment URL (with thumbnail optimization)
      const getAttachmentUrl = (attachmentField, preferThumbnail = false) => {
        if (attachmentField && Array.isArray(attachmentField) && attachmentField.length > 0) {
          const attachment = attachmentField[0];

          // Use thumbnail for better performance (especially for logos)
          if (preferThumbnail && attachment.thumbnails) {
            // Use 'large' thumbnail (512px) which is perfect for our 80x80 display
            // Falls back to 'small' if large isn't available
            return attachment.thumbnails.large?.url || attachment.thumbnails.small?.url || attachment.url;
          }

          return attachment.url;
        }
        return null;
      };

      // Helper function to format time (e.g., 1030 -> "10:30 AM"). Handles
      // after-midnight values (2500 = 1:00 AM) used for restaurants that close
      // past midnight, e.g. Big Apple Pizza.
      const formatTime = (timeNumber) => {
        if (!timeNumber) return '';
        const timeStr = timeNumber.toString().padStart(4, '0');
        let hours = parseInt(timeStr.substring(0, 2)) % 24; // 25 -> 1
        const minutes = timeStr.substring(2, 4);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
      };

      // Helper: get weekly hours from a Resource: Hours-style record. Returns an array
      // of 7 entries per day:
      //  - openRaw / closeRaw: the FM ordering window (Resource Hours open/close). The
      //    close is the "Family Meal last order" cutoff (informational + ordering gate).
      //  - operationalCloseRaw: when the restaurant is physically open until (new
      //    "[Day] - operational close" field). Falls back to closeRaw when not set.
      //    Used for the displayed hours and pickup-window clamping. May exceed 2400
      //    for after-midnight close (e.g. 2500 = 1:00 AM, Big Apple Pizza).
      //  - formatted: displayed real hours = open – operational close.
      //  - lastOrder: formatted FM last-order time (Resource close).
      const getWeeklyHours = (hoursRecord) => {
        if (!hoursRecord) return null;
        const fields = hoursRecord.fields;
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        return days.map(day => {
          const openTime = fields[`${day} - open`];
          const closeTime = fields[`${day} - close`];
          const opClose = fields[`${day} - operational close`] || closeTime; // fallback

          if (openTime && closeTime) {
            return {
              day,
              open: formatTime(openTime),
              close: formatTime(closeTime),
              openRaw: openTime,
              closeRaw: closeTime,
              operationalCloseRaw: opClose,
              operationalClose: formatTime(opClose),
              lastOrder: formatTime(closeTime),
              formatted: `${formatTime(openTime)} - ${formatTime(opClose)}`
            };
          }
          return { day, open: null, close: null, openRaw: null, closeRaw: null, operationalCloseRaw: null, operationalClose: null, lastOrder: null, formatted: 'Closed' };
        });
      };

      // Merge primary + secondary weekly hours into a single per-day entry. Each day
      // can have up to two shifts ("primary" and "secondary"). The secondary shift is
      // surfaced visually (so families see split hours) and contributes the latest
      // close time of the day for pickup-window clamping.
      const mergeWeeklyHours = (primary, secondary) => {
        if (!primary && !secondary) return null;
        if (!secondary) return primary;
        if (!primary) return secondary;
        return primary.map((p, i) => {
          const s = secondary[i];
          if (!s || !s.openRaw || !s.closeRaw) return p;
          if (!p.openRaw || !p.closeRaw) {
            return { ...s, secondaryFormatted: null };
          }
          return {
            ...p,
            secondaryOpen: s.open,
            secondaryClose: s.close,
            secondaryOpenRaw: s.openRaw,
            secondaryCloseRaw: s.closeRaw,
            secondaryFormatted: s.formatted,
            // The day's FM last-order time is the LATEST shift's close, i.e. the
            // second shift — not the first (which ...p would otherwise carry).
            lastOrder: s.lastOrder || p.lastOrder,
            formatted: `${p.formatted}, ${s.formatted}`
          };
        });
      };

      // Map hours records by restaurant ID
      const hoursByRestaurant = {};
      hoursData.records.forEach(hoursRecord => {
        const restaurantIds = hoursRecord.fields.Restaurants || [];
        restaurantIds.forEach(restaurantId => {
          hoursByRestaurant[restaurantId] = hoursRecord;
        });
      });

      const secondHoursByRestaurant = {};
      (secondHoursData.records || []).forEach(hoursRecord => {
        const restaurantIds = hoursRecord.fields.Restaurants || [];
        restaurantIds.forEach(restaurantId => {
          secondHoursByRestaurant[restaurantId] = hoursRecord;
        });
      });

      // Current time in San Francisco (America/Los_Angeles), independent of the
      // viewer's device timezone. Restaurant hours are SF-local, so open/closed must
      // be computed against SF time — otherwise a family member ordering from another
      // timezone (e.g. EST) sees the wrong open/closed status.
      const sfParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      const sfWeekday = sfParts.find(p => p.type === 'weekday')?.value;
      let sfHour = parseInt(sfParts.find(p => p.type === 'hour')?.value || '0', 10);
      if (sfHour === 24) sfHour = 0; // some engines emit '24' at midnight with hour12:false
      const sfMinute = parseInt(sfParts.find(p => p.type === 'minute')?.value || '0', 10);
      const sfNowMinutes = sfHour * 60 + sfMinute;

      // Transform restaurants data
      const transformedRestaurants = restaurantsData.records
        .filter(record => {
          const isTestRestaurant =
            record.fields['Test'] === true ||
            (record.fields['DBA Name'] || '').toLowerCase().includes("matt's bistro");

          // SFND staff testers see test restaurants AND every real In-Service
          // restaurant. Their orders to real restaurants are silently filtered out
          // of those restaurants' dashboards by the existing CBO filter, so this is
          // safe — it lets the team end-to-end test the family flow across the
          // full restaurant list while still funneling visible orders to Matt's
          // Bistro for restaurant-side QA. (Previous behavior limited staff to
          // test restaurants only, blocking real restaurant discovery testing.)
          if (isTestUser) {
            return isTestRestaurant || Boolean(record.fields['In Service']);
          }

          // Real families never see test restaurants
          if (isTestRestaurant) return false;

          // Regular restaurants must be In Service
          return Boolean(record.fields['In Service']);
        })
        .map(record => {
          const fields = record.fields;
          const isTestRestaurant =
            fields['Test'] === true ||
            (fields['DBA Name'] || '').toLowerCase().includes("matt's bistro");

          // Build menu directly from the Restaurants record's per-language Menu Item fields.
          // Photos come from `Menu Item #N Photo` attachment fields — we prefer the
          // 512px Airtable-served thumbnail (`thumbnails.large`) so menu cards stay
          // snappy without us hosting the assets ourselves. Falls through to the
          // SVG placeholder via getFallbackImage() when no photo is set.
          const menu = [];
          for (let i = 1; i <= 6; i++) {
            const en = fields[`Menu Item #${i} English`] || fields[`Menu Item #${i}`];
            if (!en) continue;
            menu.push({
              id: i,
              nameEN: en,
              nameES: fields[`Menu Item #${i} Spanish`] || en,
              nameCH: fields[`Menu Item #${i} Chinese`] || en,
              nameVI: fields[`Menu Item #${i} Vietnamese`] || en, // VI not yet on schema
              nameAR: fields[`Menu Item #${i} Arabic`] || en,
              descriptionEN: en,
              descriptionES: fields[`Menu Item #${i} Spanish`] || en,
              descriptionCH: fields[`Menu Item #${i} Chinese`] || en,
              descriptionVI: fields[`Menu Item #${i} Vietnamese`] || en,
              descriptionAR: fields[`Menu Item #${i} Arabic`] || en,
              imageUrl: getAttachmentUrl(fields[`Menu Item #${i} Photo`], true),
              // Dietary considerations (multi-select on the Restaurants table). Stored
              // in English; the master translation pass will swap these to localized
              // labels per the team's translation doc.
              dietaryConsiderations: fields[`Menu Item #${i} Dietary Considerations`] || [],
              vouchers: 1
            });
          }

          // Get hours information (merge primary + split-shift secondary)
          const hoursRecord = hoursByRestaurant[record.id];
          const secondHoursRecord = secondHoursByRestaurant[record.id];
          const weeklyHours = mergeWeeklyHours(
            getWeeklyHours(hoursRecord),
            getWeeklyHours(secondHoursRecord)
          );

          // Today's hours: prefer the merged weekly schedule (so split shifts render),
          // and fall back to the Open/Close rollup if Resource: Hours has nothing for today.
          const todayName = sfWeekday;
          const todayWeekly = weeklyHours
            ? weeklyHours.find(d => d.day === todayName)
            : null;
          const todayOpen = fields.Open;
          const todayClose = fields.Close;
          const todayHours = (todayWeekly && todayWeekly.openRaw && todayWeekly.closeRaw)
            ? todayWeekly.formatted
            : (todayOpen && todayClose ? `${todayOpen} - ${todayClose}` : 'Hours not available');

          // Check if restaurant is currently open by comparing current time with Open/Close times
          const isOpen = (() => {
            // Test restaurants are always shown as open
            if (isTestRestaurant) return true;

            if (!todayOpen || !todayClose || !fields['In Service']) return false;

            // Parse time strings like "10:30 AM" or "9:00 PM"
            const parseTime = (timeStr) => {
              const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
              if (!match) return null;

              let hours = parseInt(match[1]);
              const minutes = parseInt(match[2]);
              const period = match[3].toUpperCase();

              // Convert to 24-hour format
              if (period === 'PM' && hours !== 12) hours += 12;
              if (period === 'AM' && hours === 12) hours = 0;

              return hours * 60 + minutes; // Return total minutes from midnight
            };

            const currentMinutes = sfNowMinutes; // SF-local minutes since midnight
            const openMinutes = parseTime(todayOpen);
            const closeMinutes = parseTime(todayClose);

            if (openMinutes === null || closeMinutes === null) return false;

            // Overnight hours (close past midnight, e.g. close < open): open if after
            // open OR before close.
            if (closeMinutes < openMinutes) {
              return currentMinutes >= openMinutes || currentMinutes <= closeMinutes;
            }
            // Check if current time is between open and close
            return currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
          })();

          return {
            id: record.id,
            dbaName: fields['DBA Name'] || fields.Name || 'Unknown Restaurant',
            name: fields['DBA Name'] || fields.Name || 'Unknown Restaurant', // Add name field for consistency
            cuisine: fields['Cuisine Type'] || 'Various',
            neighborhood: fields.Neighborhoods || '',
            address: fields['Street Address'] || '',
            phone: fields['Restaurant Phone'] || fields['Phone'] || '',
            rating: fields.Rating || 4.5,
            deliveryTime: fields['Delivery Time'] || '30-40 min',
            logo: getAttachmentUrl(fields['Restaurant Logo'], true), // Use thumbnail for fast loading
            image: '🍽️', // Fallback emoji (kept for backwards compatibility)
            isOpen: isOpen,
            // Test restaurants bypass the In Service check too — staff testers can't flip
            // In Service on the real record without the SMS app picking it up.
            inService: isTestRestaurant ? true : Boolean(fields['In Service']),
            hours: todayHours, // Today's hours from rollup fields
            weeklyHours: weeklyHours, // Full weekly schedule
            zipcode: fields.Zipcode || '',
            dailyMealLimit: fields['Daily Meal Limit'] || 50,
            mealsOrderedToday: fields['Meals Ordered Today'] || 0,
            menu: menu
          };
        });

      setRestaurants(transformedRestaurants);
      setDemoMode(false);
      setLoading(false);
      // Set animation flag immediately to prevent flicker
      setHasAnimated(true);
      if (profileId) writeRestaurantCache(profileId, transformedRestaurants);
    } catch (error) {
      console.error('[Airtable] Error loading restaurants:', error);
      // Only fall back to demo data if we don't already have cached restaurants
      // on screen — otherwise the cached list is a strictly better fallback
      // than the demo set, and replacing it would be jarring mid-session.
      if (!cached) {
        setError(t.errLoadRestaurants);
        loadDemoData();
      }
    }
  };

  const loadDemoData = () => {
    const demoRestaurants = [
      {
        id: 'demo1',
        dbaName: "Golden Dragon",
        name: "Golden Dragon",
        cuisine: "Chinese",
        neighborhood: "Chinatown",
        address: "123 Grant Ave, San Francisco, CA 94108",
        phone: "(415) 555-0123",
        rating: 4.5,
        deliveryTime: "25-35 min",
        image: "🏮",
        logo: null, // No logo in demo mode - will show placeholder
        isOpen: true,
        inService: true,
        hours: "11:00 AM - 9:00 PM",
        weeklyHours: [
          { day: 'Monday', open: '11:00 AM', close: '9:00 PM', formatted: '11:00 AM - 9:00 PM' },
          { day: 'Tuesday', open: '11:00 AM', close: '9:00 PM', formatted: '11:00 AM - 9:00 PM' },
          { day: 'Wednesday', open: '11:00 AM', close: '9:00 PM', formatted: '11:00 AM - 9:00 PM' },
          { day: 'Thursday', open: '11:00 AM', close: '9:00 PM', formatted: '11:00 AM - 9:00 PM' },
          { day: 'Friday', open: '11:00 AM', close: '10:00 PM', formatted: '11:00 AM - 10:00 PM' },
          { day: 'Saturday', open: '11:00 AM', close: '10:00 PM', formatted: '11:00 AM - 10:00 PM' },
          { day: 'Sunday', open: '12:00 PM', close: '8:00 PM', formatted: '12:00 PM - 8:00 PM' }
        ],
        zipcode: "94110",
        dailyMealLimit: 50,
        mealsOrderedToday: 12,
        menu: [
          { 
            id: 1, 
            nameEN: "Orange Chicken Family Pack", 
            nameES: "Paquete Familiar de Pollo a la Naranja", 
            nameCH: "橙汁雞套餐",
            descriptionEN: "Crispy chicken in sweet orange sauce with steamed rice and vegetables. Serves 4-5 people.",
            descriptionES: "Pollo crujiente en salsa dulce de naranja con arroz al vapor y vegetales. Para 4-5 personas.",
            descriptionCH: "香脆鸡肉配甜橙酱、蒸米饭和蔬菜。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1603073203844-6f0f048802b5?w=400&h=300&fit=crop",
            vouchers: 1 
          },
          { 
            id: 2, 
            nameEN: "Kung Pao Chicken Family Pack", 
            nameES: "Paquete Familiar de Pollo Kung Pao", 
            nameCH: "宮保雞丁套餐",
            descriptionEN: "Spicy stir-fried chicken with peanuts, vegetables, and chili peppers. Includes rice. Serves 4-5.",
            descriptionES: "Pollo salteado picante con maní, vegetales y chiles. Incluye arroz. Para 4-5 personas.",
            descriptionCH: "辣炒鸡丁配花生、蔬菜和辣椒。含米饭。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=400&h=300&fit=crop",
            vouchers: 1 
          },
          { 
            id: 3, 
            nameEN: "Vegetable Chow Mein Family Pack", 
            nameES: "Paquete Familiar de Chow Mein de Verduras", 
            nameCH: "蔬菜炒麵套餐",
            descriptionEN: "Stir-fried noodles with mixed vegetables in savory sauce. Vegetarian-friendly. Serves 4-5.",
            descriptionES: "Fideos salteados con vegetales mixtos en salsa sabrosa. Vegetariano. Para 4-5 personas.",
            descriptionCH: "炒面配什锦蔬菜和美味酱汁。素食。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=400&h=300&fit=crop",
            vouchers: 1 
          }
        ]
      },
      {
        id: 'demo2',
        dbaName: "Taqueria El Sol",
        name: "Taqueria El Sol",
        cuisine: "Latin",
        neighborhood: "Mission",
        address: "2800 Mission St, San Francisco, CA 94110",
        phone: "(415) 555-0456",
        rating: 4.7,
        deliveryTime: "20-30 min",
        image: "🌮",
        logo: null, // No logo in demo mode - will show placeholder
        isOpen: false,
        inService: true,
        hours: "12:00 PM - 10:00 PM",
        weeklyHours: [
          { day: 'Monday', open: '12:00 PM', close: '10:00 PM', formatted: '12:00 PM - 10:00 PM' },
          { day: 'Tuesday', open: '12:00 PM', close: '10:00 PM', formatted: '12:00 PM - 10:00 PM' },
          { day: 'Wednesday', open: '12:00 PM', close: '10:00 PM', formatted: '12:00 PM - 10:00 PM' },
          { day: 'Thursday', open: '12:00 PM', close: '10:00 PM', formatted: '12:00 PM - 10:00 PM' },
          { day: 'Friday', open: '12:00 PM', close: '11:00 PM', formatted: '12:00 PM - 11:00 PM' },
          { day: 'Saturday', open: '12:00 PM', close: '11:00 PM', formatted: '12:00 PM - 11:00 PM' },
          { day: 'Sunday', open: null, close: null, formatted: 'Closed' }
        ],
        zipcode: "94110",
        dailyMealLimit: 40,
        mealsOrderedToday: 0,
        menu: [
          { 
            id: 1, 
            nameEN: "Taco Family Pack", 
            nameES: "Paquete Familiar de Tacos", 
            nameCH: "塔可套餐",
            descriptionEN: "12 tacos with choice of meat, tortillas, salsa, and toppings. Serves 4-5 people.",
            descriptionES: "12 tacos con carne a elección, tortillas, salsa y guarniciones. Para 4-5 personas.",
            descriptionCH: "12个玉米饼，可选肉类、玉米饼、莎莎酱和配料。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=400&h=300&fit=crop",
            vouchers: 1 
          },
          { 
            id: 2, 
            nameEN: "Burrito Family Pack", 
            nameES: "Paquete Familiar de Burritos", 
            nameCH: "捲餅套餐",
            descriptionEN: "4 large burritos with rice, beans, meat, cheese, and vegetables. Serves 4 people.",
            descriptionES: "4 burritos grandes con arroz, frijoles, carne, queso y vegetales. Para 4 personas.",
            descriptionCH: "4个大号墨西哥卷饼，配米饭、豆类、肉类、奶酪和蔬菜。供4人食用。",
            imageUrl: "https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=400&h=300&fit=crop",
            vouchers: 1 
          }
        ]
      },
      {
        id: 'demo3',
        dbaName: "Mama's Italian Kitchen",
        name: "Mama's Italian Kitchen",
        cuisine: "American",
        neighborhood: "North Beach",
        address: "456 Columbus Ave, San Francisco, CA 94133",
        phone: "(415) 555-0789",
        rating: 4.6,
        deliveryTime: "30-40 min",
        image: "🍝",
        logo: null, // No logo in demo mode - will show placeholder
        isOpen: true,
        inService: true,
        hours: "10:00 AM - 8:00 PM",
        weeklyHours: [
          { day: 'Monday', open: '10:00 AM', close: '8:00 PM', formatted: '10:00 AM - 8:00 PM' },
          { day: 'Tuesday', open: '10:00 AM', close: '8:00 PM', formatted: '10:00 AM - 8:00 PM' },
          { day: 'Wednesday', open: null, close: null, formatted: 'Closed' },
          { day: 'Thursday', open: '10:00 AM', close: '8:00 PM', formatted: '10:00 AM - 8:00 PM' },
          { day: 'Friday', open: '10:00 AM', close: '9:00 PM', formatted: '10:00 AM - 9:00 PM' },
          { day: 'Saturday', open: '10:00 AM', close: '9:00 PM', formatted: '10:00 AM - 9:00 PM' },
          { day: 'Sunday', open: '11:00 AM', close: '7:00 PM', formatted: '11:00 AM - 7:00 PM' }
        ],
        zipcode: "94110",
        dailyMealLimit: 35,
        mealsOrderedToday: 8,
        menu: [
          { 
            id: 1, 
            nameEN: "Spaghetti & Meatballs Family Pack", 
            nameES: "Paquete Familiar de Espagueti", 
            nameCH: "意大利麵套餐",
            descriptionEN: "Classic spaghetti with homemade meatballs, marinara sauce, and garlic bread. Serves 4-5.",
            descriptionES: "Espagueti clásico con albóndigas caseras, salsa marinara y pan de ajo. Para 4-5 personas.",
            descriptionCH: "经典意大利面配自制肉丸、番茄酱和大蒜面包。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1622973536968-3ead9e780960?w=400&h=300&fit=crop",
            vouchers: 1 
          },
          { 
            id: 2, 
            nameEN: "Lasagna Family Pack", 
            nameES: "Paquete Familiar de Lasaña", 
            nameCH: "千層麵套餐",
            descriptionEN: "Layers of pasta, meat sauce, ricotta, and mozzarella cheese. Includes side salad. Serves 4-5.",
            descriptionES: "Capas de pasta, salsa de carne, ricotta y queso mozzarella. Incluye ensalada. Para 4-5 personas.",
            descriptionCH: "多层面食、肉酱、乳清干酪和马苏里拉奶酪。含配菜沙拉。供4-5人食用。",
            imageUrl: "https://images.unsplash.com/photo-1574894709920-11b28e7367e3?w=400&h=300&fit=crop",
            vouchers: 1 
          }
        ]
      },
      {
        id: 'demo4',
        dbaName: "Pho Vung Tau",
        name: "Pho Vung Tau",
        cuisine: "Vietnamese",
        neighborhood: "Tenderloin",
        address: "620 Larkin St, San Francisco, CA 94102",
        phone: "(415) 555-0321",
        rating: 4.8,
        deliveryTime: "25-35 min",
        image: "🍲",
        logo: null, // No logo in demo mode - will show placeholder
        isOpen: true,
        inService: true,
        hours: "9:00 AM - 9:00 PM",
        weeklyHours: [
          { day: 'Monday', open: '9:00 AM', close: '9:00 PM', formatted: '9:00 AM - 9:00 PM' },
          { day: 'Tuesday', open: '9:00 AM', close: '9:00 PM', formatted: '9:00 AM - 9:00 PM' },
          { day: 'Wednesday', open: '9:00 AM', close: '9:00 PM', formatted: '9:00 AM - 9:00 PM' },
          { day: 'Thursday', open: '9:00 AM', close: '9:00 PM', formatted: '9:00 AM - 9:00 PM' },
          { day: 'Friday', open: '9:00 AM', close: '10:00 PM', formatted: '9:00 AM - 10:00 PM' },
          { day: 'Saturday', open: '8:00 AM', close: '10:00 PM', formatted: '8:00 AM - 10:00 PM' },
          { day: 'Sunday', open: '8:00 AM', close: '9:00 PM', formatted: '8:00 AM - 9:00 PM' }
        ],
        zipcode: "94110",
        dailyMealLimit: 45,
        mealsOrderedToday: 15,
        menu: [
          {
            id: 1,
            nameEN: "Pho Family Pack",
            nameES: "Paquete Familiar de Pho",
            nameCH: "越南河粉套餐",
            nameVI: "Gói Phở Gia Đình",
            nameAR: "حزمة فو العائلية",
            descriptionEN: "Traditional beef noodle soup with herbs, lime, and fresh vegetables. Serves 4-5 people.",
            descriptionES: "Sopa tradicional de fideos con carne, hierbas, lima y vegetales frescos. Para 4-5 personas.",
            descriptionCH: "传统牛肉米粉汤配香草、青柠和新鲜蔬菜。供4-5人食用。",
            descriptionVI: "Súp mì bò truyền thống với rau thơm, chanh và rau tươi. Phục vụ 4-5 người.",
            descriptionAR: "شوربة المعكرونة بلحم البقر التقليدية مع الأعشاب والليمون والخضروات الطازجة. تكفي 4-5 أشخاص.",
            imageUrl: "https://images.unsplash.com/photo-1591814468924-caf88d1232e1?w=400&h=300&fit=crop",
            vouchers: 1
          },
          {
            id: 2,
            nameEN: "Banh Mi Family Pack",
            nameES: "Paquete Familiar de Banh Mi",
            nameCH: "越南三明治套餐",
            nameVI: "Gói Bánh Mì Gia Đình",
            nameAR: "حزمة بان مي العائلية",
            descriptionEN: "6 Vietnamese sandwiches with meat, pickled vegetables, and fresh herbs. Serves 4-5.",
            descriptionES: "6 sándwiches vietnamitas con carne, vegetales encurtidos y hierbas frescas. Para 4-5 personas.",
            descriptionCH: "6个越南三明治，配肉类、腌菜和新鲜香草。供4-5人食用。",
            descriptionVI: "6 bánh mì Việt Nam với thịt, rau củ ngâm chua và rau thơm tươi. Phục vụ 4-5 người.",
            descriptionAR: "6 شطائر فيتنامية مع اللحم والخضروات المخللة والأعشاب الطازجة. تكفي 4-5 أشخاص.",
            imageUrl: "https://images.unsplash.com/photo-1605336248881-c0b35821613e?w=400&h=300&fit=crop",
            vouchers: 1
          }
        ]
      }
    ];

    setRestaurants(demoRestaurants);
    setDemoMode(true);
    setLoading(false);
    setError(null);
    // Set animation flag immediately to prevent flicker
    setHasAnimated(true);
  };

  // Filter by neighborhood, cuisine, and search query, then sort open restaurants
  // first and alphabetically within each group.
  // Use useMemo to prevent unnecessary recalculations on every render
  const sortedRestaurants = useMemo(() => {
    const filtered = restaurants.filter(r => {
      const neighborhoodMatch = selectedNeighborhood === 'all' || r.neighborhood === selectedNeighborhood;
      const cuisineMatch = selectedCuisine === 'all' || r.cuisine === selectedCuisine;
      const searchMatch = searchQuery === '' ||
        (r.name && r.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (r.dbaName && r.dbaName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (r.cuisine && r.cuisine.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (r.neighborhood && r.neighborhood.toLowerCase().includes(searchQuery.toLowerCase()));
      return neighborhoodMatch && cuisineMatch && searchMatch;
    });

    // Open restaurants first, then closed — alphabetical by name within each group.
    return filtered.sort((a, b) => {
      if (Boolean(a.isOpen) !== Boolean(b.isOpen)) {
        return a.isOpen ? -1 : 1;
      }
      const nameA = a.name || a.dbaName || '';
      const nameB = b.name || b.dbaName || '';
      return nameA.localeCompare(nameB);
    });
  }, [restaurants, selectedNeighborhood, selectedCuisine, searchQuery]);

  // Memoize unique neighborhoods and cuisines to prevent recalculation on every render
  const uniqueNeighborhoods = useMemo(() => {
    return [...new Set(restaurants.map(r => r.neighborhood).filter(Boolean))].sort();
  }, [restaurants]);

  const uniqueCuisines = useMemo(() => {
    return [...new Set(restaurants.map(r => r.cuisine).filter(Boolean))].sort();
  }, [restaurants]);

  // Check if filters are active
  const hasActiveFilters = selectedNeighborhood !== 'all' || selectedCuisine !== 'all' || searchQuery !== '';

  // Use TRANSLATIONS constant defined outside component (prevents recreation on every render)
  const t = TRANSLATIONS[language];
  // Build a Maps-friendly address string. Street address alone has hit wrong cities
  // (one alpha tester out of state got pulled to a same-named street elsewhere), so
  // we always append "San Francisco, CA" and the zip when available.
  const mapsAddress = (street, zipcode) => {
    const parts = [street, 'San Francisco, CA', zipcode].filter(Boolean);
    return parts.join(', ');
  };

  // Heuristic filter for dietary restriction strings until the Airtable schema
  // returns a single language per call. Categorizes each string by the dominant
  // Unicode script and keeps only entries matching the user's selected language.
  // English includes Spanish (both Latin) — those collide and is acceptable for now.
  const filterDietaryByLanguage = (values, lang) => {
    if (!Array.isArray(values)) return [];
    const detect = (s) => {
      if (!s || typeof s !== 'string') return 'unknown';
      if (/[一-鿿]/.test(s)) return 'zh';            // CJK
      if (/[؀-ۿ]/.test(s)) return 'ar';            // Arabic
      // Vietnamese-specific: đ, ạ, ặ, ẵ, etc. (precomposed Latin + Vietnamese marks)
      if (/[À-ỹ]/.test(s) && /[đĐăĂâÂêÊôÔơƠưƯ]/.test(s)) return 'vi';
      return 'latin'; // English or Spanish
    };
    return values.filter(v => {
      const d = detect(v);
      if (lang === 'zh') return d === 'zh';
      if (lang === 'ar') return d === 'ar';
      if (lang === 'vi') return d === 'vi' || d === 'latin';
      // en + es both fall under Latin
      return d === 'latin';
    });
  };

  const getMenuItemName = (item) => {
    if (language === 'es' && item.nameES) return item.nameES;
    if (language === 'zh' && item.nameCH) return item.nameCH;
    if (language === 'vi' && item.nameVI) return item.nameVI;
    if (language === 'ar' && item.nameAR) return item.nameAR;
    return item.nameEN;
  };

  const getMenuItemDescription = (item) => {
    if (language === 'es' && item.descriptionES) return item.descriptionES;
    if (language === 'zh' && item.descriptionCH) return item.descriptionCH;
    if (language === 'vi' && item.descriptionVI) return item.descriptionVI;
    if (language === 'ar' && item.descriptionAR) return item.descriptionAR;
    return item.descriptionEN;
  };

  // Calculate vouchers in cart and remaining
  const vouchersInCart = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.vouchers, 0);
  }, [cart]);

  const vouchersRemaining = userProfile.vouchersRemaining - vouchersInCart;

  const addToCart = useCallback(async (item, restaurant) => {
    if (vouchersRemaining <= 0) return;

    // Enforce one-restaurant-per-order: prompt before clearing cart from another restaurant
    if (cart.length > 0 && cart[0].restaurantId !== restaurant.id) {
      // TODO(translate): "Switch restaurants?" title and "Start new order" confirm
      // label are still English-only. Add to Katie's next translation pass.
      const ok = await confirm({
        title: 'Switch restaurants?',
        message: `${t.onlyOneRestaurantAtATime} Your cart has items from ${cart[0].restaurantName}. Adding this will clear it and start a new order from ${restaurant.dbaName}.`,
        confirmLabel: 'Start new order',
        cancelLabel: t.cancel,
        variant: 'danger',
      });
      if (!ok) return;
      setCart([{ ...item, restaurantName: restaurant.dbaName, restaurantId: restaurant.id }]);
      return;
    }

    setCart(prevCart => [...prevCart, { ...item, restaurantName: restaurant.dbaName, restaurantId: restaurant.id }]);
  }, [vouchersRemaining, cart, confirm, t]);

  const removeFromCart = useCallback((index) => {
    setCart(prevCart => prevCart.filter((_, i) => i !== index));
  }, []);

  const placeOrder = async () => {
    if (cart.length === 0 || !userProfile || placingOrder) return;
    setPlacingOrder(true);
    setOrderError('');

    try {
      // Group cart items into Menu Item #N Amount counts (positions 1..6)
      const amounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      for (const item of cart) {
        if (item.id >= 1 && item.id <= 6) amounts[item.id] += 1;
      }

      const restaurantId = cart[0].restaurantId;
      const orderRestaurant = restaurants.find(r => r.id === restaurantId) || selectedRestaurant;
      const vouchersUsed = cart.reduce((sum, item) => sum + item.vouchers, 0);

      // Dev-only short-circuit: skip the Airtable write when running locally without creds
      if (!import.meta.env.PROD && demoMode) {
        const confirmation = buildConfirmation('ORD' + Math.random().toString(36).slice(2, 11).toUpperCase(),
          orderRestaurant, cart, vouchersUsed);
        setOrderConfirmation(confirmation);
        setCart([]);
        setCurrentView('confirmation');
        return;
      }

      // Place the order through the proxy. The server re-reads the authoritative
      // voucher balance, rejects overdraw, writes the Order Log row, and decrements
      // vouchers — then returns the new order id and remaining balance.
      const result = await api.placeOrder({ restaurantId, amounts });
      const newRemaining = result.vouchersRemaining;
      console.log('[Order] Order Log row created:', result.id);

      // Mirror the server-side decrement locally so the UI updates immediately
      setUserProfile(prev => prev ? { ...prev, vouchersRemaining: newRemaining } : prev);

      const confirmation = buildConfirmation(result.id, orderRestaurant, cart, vouchersUsed);
      setOrderConfirmation(confirmation);
      setCart([]);
      setCurrentView('confirmation');
      // Refresh the Orders tab so the new order appears as active.
      // Delay a beat for Airtable's Status automation to finish.
      setTimeout(() => loadOrders(), 1500);
    } catch (err) {
      console.error('[Order] Failed to place order:', err);
      setOrderError(`Could not place order: ${err.message}. Please try again or contact support.`);
    } finally {
      setPlacingOrder(false);
    }
  };

  const buildConfirmation = (orderId, orderRestaurant, items, vouchersUsed) => {
    const placedAt = new Date();
    const totalMeals = items.length;
    const window = computePickupWindow(placedAt, totalMeals, orderRestaurant);
    return {
      orderId,
      restaurant: orderRestaurant,
      restaurantName: orderRestaurant.dbaName,
      restaurantAddress: orderRestaurant.address || '',
      items,
      totalMeals,
      familyCode: userProfile.familyCode,
      vouchersRemaining: userProfile.vouchersRemaining - vouchersUsed,
      pickupEarliestAt: window.earliest,
      pickupLatestAt: window.latest,
      pickupClampedByClose: window.clampedByClose,
      pickupAsap: window.asap,
      resetDate: userProfile.renewalDate || '',
      supportPhone: '(415) 480-1185',
      placedAt,
      orderTime: placedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
  };

  // Compute a pickup window based on order size and the restaurant's operational
  // close time. Size rules from alpha feedback: 1–6 meals → 30/60 min, 7+ → 60/90 min.
  // Near closing the window is squeezed: if the restaurant closes within 30 min of
  // the order, pickup becomes "as soon as possible" (asap) with a pick-up-by of the
  // close time; otherwise pickup-by is clamped to the close time.
  const computePickupWindow = (placedAt, totalMeals, orderRestaurant) => {
    const earliestMin = totalMeals <= 6 ? 30 : 60;
    const latestMin = totalMeals <= 6 ? 60 : 90;
    let earliest = new Date(placedAt.getTime() + earliestMin * 60000);
    let latest = new Date(placedAt.getTime() + latestMin * 60000);
    const closeAt = todayCloseDate(orderRestaurant, placedAt);
    let clampedByClose = false;
    let asap = false;
    if (closeAt) {
      const minsToClose = (closeAt.getTime() - placedAt.getTime()) / 60000;
      if (minsToClose < 30) {
        // Closing very soon — tell them to come as soon as possible, by close.
        asap = true;
        clampedByClose = true;
        earliest = placedAt;
        latest = closeAt;
      } else {
        if (latest > closeAt) { latest = closeAt; clampedByClose = true; }
        if (earliest > closeAt) { earliest = closeAt; clampedByClose = true; }
      }
    }
    return { earliest, latest, clampedByClose, asap };
  };

  // Convert today's operational close into a Date relative to the order time.
  // Prefers the new operational-close field, then split-shift close, then the FM
  // close. Values ≥ 2400 (e.g. 2500 = 1 AM) and close-before-open both roll to the
  // next day (Big Apple Pizza).
  const todayCloseDate = (orderRestaurant, placedAt) => {
    if (!orderRestaurant?.weeklyHours) return null;
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayName = dayNames[placedAt.getDay()];
    const today = orderRestaurant.weeklyHours.find(d => d.day === todayName);
    if (!today) return null;
    const closeRaw = today.operationalCloseRaw || today.secondaryCloseRaw || today.closeRaw;
    const openRaw = today.secondaryOpenRaw || today.openRaw;
    if (!closeRaw || !openRaw) return null;
    const closeH = Math.floor(closeRaw / 100); // 25 (for 2500) is fine — setHours rolls over
    const closeM = closeRaw % 100;
    const openH = Math.floor(openRaw / 100);
    const result = new Date(placedAt);
    result.setHours(closeH, closeM, 0, 0);
    // Overnight close stored as a small hour (e.g. 0100) ⇒ next day
    if (closeH < openH) {
      result.setDate(result.getDate() + 1);
    }
    return result;
  };

  // Memoize vouchers calculation to prevent recalculation on every render

  // Order Confirmation View
  const ConfirmationView = () => (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={tealBgStyle}>
          <Check size={48} className="text-midnight" />
        </div>
        <h2 className="text-title-lg font-bold text-gray-900 mb-2">{t.orderConfirmed}</h2>
        <p className="text-gray-600">{t.orderPlacedAt} {orderConfirmation.orderTime}</p>
      </div>

      {/* Family Code - Most Important */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 text-center border-4" style={tealBorderStyle}>
        <p className="text-body-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">{t.yourFamilyCode}</p>
        <div className="text-display font-bold tracking-widest mb-3" style={tealColorStyle} dir="ltr">
          {orderConfirmation.familyCode}
        </div>
        <p className="text-body-sm text-gray-600">{t.showThisCode}</p>
      </div>

      {/* Pickup Instructions */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-body-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MapPin size={20} style={tealColorStyle} />
          {t.pickupInstructions}
        </h3>

        {/* Restaurant Info */}
        <div className="mb-5">
          <p className="text-body-sm font-semibold text-gray-600 mb-1">{t.restaurantLocation}</p>
          <p className="text-body-lg font-bold text-gray-900 mb-1">{orderConfirmation.restaurantName}</p>
          <p className="text-gray-600 mb-1">{orderConfirmation.restaurantAddress}</p>
          {orderConfirmation.restaurant.phone && (
            <a
              href={`tel:${orderConfirmation.restaurant.phone}`}
              className="flex items-center gap-1 text-body-sm mb-1 hover:underline"
              style={tealColorStyle}
            >
              <Phone size={14} />
              <span>{orderConfirmation.restaurant.phone}</span>
            </a>
          )}
          <p className="text-label text-gray-500 mb-3">{t.pickupOnly}</p>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(mapsAddress(orderConfirmation.restaurantAddress, orderConfirmation.restaurant?.zipcode))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-midnight px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
            style={tealBgStyle}
          >
            <MapPin size={16} />
            {t.getDirections}
          </a>
        </div>

        {/* Pickup Time Window */}
        <div className="pt-5 border-t border-gray-200 mb-5">
          <p className="text-body-sm font-semibold text-gray-600 mb-2">{t.pickupWindow}</p>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 space-y-1">
            <p className="text-gray-900 font-medium">
              {t.pickupAt}{' '}
              <span className="font-bold" style={tealColorStyle}>
                {orderConfirmation.pickupAsap
                  ? t.asSoonAsPossible
                  : orderConfirmation.pickupEarliestAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            </p>
            <p className="text-gray-700 text-body-sm">
              {t.pickupBy}{' '}
              <span className="font-semibold">
                {orderConfirmation.pickupLatestAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              {orderConfirmation.pickupClampedByClose && !orderConfirmation.pickupAsap && (
                <span className="text-label text-gray-600"> ({t.unlessCloseSooner})</span>
              )}
            </p>
          </div>
        </div>

        {/* Closing Hours Warning */}
        {orderConfirmation.restaurant.hours && (
          <div className="pt-5 border-t border-gray-200">
            <p className="text-body-sm text-gray-600">
              <Clock size={14} className="inline me-1" />
              <span className="font-semibold">{t.restaurantHours}:</span> {orderConfirmation.restaurant.hours}
            </p>
          </div>
        )}
      </div>

      {/* Vouchers Remaining */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="text-center">
          <p className="text-gray-700 mb-2">
            {t.youNowHave} <span className="text-title-lg font-bold mx-2" style={tealColorStyle}>{orderConfirmation.vouchersRemaining}</span> {orderConfirmation.vouchersRemaining === 1 ? t.mealLeftBefore : t.mealsLeftBefore}
          </p>
          <p className="text-body-sm text-gray-600">{orderConfirmation.resetDate}</p>
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-body-lg font-bold text-gray-900 mb-4">{t.yourOrder}</h3>
        <div className="space-y-2">
          {(() => {
            const grouped = orderConfirmation.items.reduce((acc, item) => {
              const key = item.id || getMenuItemName(item);
              if (!acc[key]) acc[key] = { item, count: 0 };
              acc[key].count += 1;
              return acc;
            }, {});
            return Object.values(grouped).map(({ item, count }, idx) => (
              <div key={idx} className="text-gray-700 flex items-start gap-2">
                <Check size={16} style={tealColorStyle} className="flex-shrink-0 mt-1" />
                <span>
                  <span className="font-bold" style={tealColorStyle}>{count}×</span> {getMenuItemName(item)}
                </span>
              </div>
            ));
          })()}
        </div>
        <div className="pt-3 mt-3 border-t border-gray-200">
          <div className="flex justify-between font-bold text-gray-900">
            <span>Total</span>
            <span>{orderConfirmation.totalMeals} meal{orderConfirmation.totalMeals > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Support Info */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-6">
        <p className="text-body-sm text-gray-700 leading-relaxed">
          {t.needSupport}{' '}
          <a href={`tel:${orderConfirmation.supportPhone}`} className="font-bold hover:underline" style={tealColorStyle}>
            {orderConfirmation.supportPhone}
          </a>
        </p>
      </div>

      {/* Action Button */}
      <button
        onClick={() => {
          setOrderConfirmation(null);
          navigateTo('home', 'back');
        }}
        className="w-full h-touch-md text-midnight rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
        style={tealBgStyle}
      >
        {t.goHome}
      </button>
    </div>
  );

  const HelpView = () => {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        <button
          onClick={() => navigateTo('home', 'back')}
          style={tealColorStyle}
          className="mb-4 -ms-2 px-2 min-h-touch font-semibold inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
        >
          {backArrow} Back
        </button>

        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={tealBgStyle}>
            <HelpCircle size={48} className="text-midnight" />
          </div>
          <h2 className="text-title-lg font-bold text-gray-900 mb-2">{t.needHelp}</h2>
          <p className="text-gray-600">{t.contactSupport}</p>
        </div>

        {/* FAQ first — let families self-serve before surfacing the call center,
            which now lives at the bottom of the page (Celine's support-page feedback). */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h3 className="text-body-lg font-bold text-gray-900 mb-5">{t.commonQuestions}</h3>

          <div className="space-y-5">
            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-bold text-gray-900 mb-2">{t.howToOrder}</h4>
              <p className="text-body-sm text-gray-700 leading-relaxed">{t.howToOrderAnswer}</p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-bold text-gray-900 mb-2">{t.wherePickup}</h4>
              <p className="text-body-sm text-gray-700 leading-relaxed">{t.wherePickupAnswer}</p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-bold text-gray-900 mb-2">{t.whenPickup}</h4>
              <p className="text-body-sm text-gray-700 leading-relaxed">{t.whenPickupAnswer}</p>
            </div>

            <div className="pb-5 border-b border-gray-200">
              <h4 className="font-bold text-gray-900 mb-2">{t.whatBring}</h4>
              <p className="text-body-sm text-gray-700 leading-relaxed">{t.whatBringAnswer}</p>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-2">{t.howManyMeals}</h4>
              <p className="text-body-sm text-gray-700 leading-relaxed">{t.howManyMealsAnswer}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border-2" style={tealBorderStyle}>
          <h3 className="text-body-lg font-bold text-gray-900 mb-4 text-center">{t.callUs}</h3>
          <a
            href="tel:+14154801185"
            className="block text-center"
          >
            <div className="text-display font-bold mb-3" style={tealColorStyle}>
              (415) 480-1185
            </div>
          </a>
          <p className="text-body-sm text-gray-700 text-center leading-relaxed mb-4">
            {t.extensions}
          </p>
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-body-sm font-semibold text-gray-600 mb-1">{t.officeHours}</p>
            <p className="text-gray-900 font-medium">{t.mondayFriday}, {t.hours}</p>
          </div>
          <p className="text-body-sm text-gray-700 leading-relaxed mt-4 pt-4 border-t border-gray-200">
            {t.voicemailInstructions}
          </p>
        </div>

        <div className="mt-6">
          <a
            href="tel:+14154801185"
            className="w-full h-touch-md flex items-center justify-center text-midnight rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
            style={tealBgStyle}
          >
            <Phone size={20} className="inline me-2 align-middle" />
            {t.callUs}
          </a>
        </div>
      </div>
    );
  };

  // Header Component
  // Header — rendered inline (not as <Header />) so React reconciles it as
  // part of the parent tree rather than remounting on every state change.
  // Remount caused the sticky header to flash on every nav tap (and made the
  // bottom nav's view-change feel like the chrome was reloading). Same pattern
  // as renderMenuView / renderCartView.
  const renderHeader = () => (
    <div className="bg-white sticky top-0 z-50 shadow-elevation-2">
      {/* Brand bar with gradient */}
      <div className="h-1" style={{
        background: GRADIENTS.brandBar
      }} />

      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo + app name */}
          <div className="flex items-center gap-3 min-w-0">
            <SFNewDealLogo size="medium" />
            <span className="text-label sm:text-body-sm font-semibold text-gray-700 leading-tight">
              {t.programName}
            </span>
          </div>

          {/* Language selector and logout */}
          <div className="flex items-center gap-3">
            {demoMode && (
              <span className="text-label font-semibold px-2 py-0.5 rounded" style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E'
              }}>
                DEMO
              </span>
            )}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-label font-medium px-3 min-h-touch rounded border focus:outline-none"
              style={{
                borderColor: '#E5E7EB',
                color: '#374151'
              }}
              aria-label="Language"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="zh">中文</option>
              <option value="vi">Tiếng Việt</option>
              <option value="ar">العربية</option>
            </select>
            <button
              onClick={handleLogout}
              className="w-touch h-touch flex items-center justify-center rounded-lg transition-all hover:bg-gray-100"
              style={{ color: BRAND_COLORS.turquoise }}
              title={t.logout}
              aria-label={t.logout}
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Orders View
  const OrdersView = () => {
    const now = new Date();
    const isSameLocalDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const todaysOrders = orders.filter(o => o.createdTime && isSameLocalDay(o.createdTime, now));
    const previousOrders = orders.filter(o => o.createdTime && !isSameLocalDay(o.createdTime, now));

    const renderOrderCard = (order) => {
      const totalMeals = order.vouchersUsed || order.menuItems?.length || 1;
      const { earliest: pickupTime, asap: pickupAsap } = computePickupWindow(
        order.createdTime,
        totalMeals,
        order.restaurant
      );
      const isToday = isSameLocalDay(order.createdTime, now);

      return (
        <div key={order.id} className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Order Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Store size={20} style={tealColorStyle} />
                  <h3 className="text-body-lg font-bold text-gray-900">{order.restaurantName}</h3>
                </div>
                <p className="text-body-sm text-gray-500">
                  {t.orderNumber}{order.orderNumber}
                </p>
              </div>
            </div>

            {/* Family Code (only show for today's orders — historical orders are already redeemed) */}
            {isToday && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-label text-gray-600 mb-1">{t.yourFamilyCode}</p>
                <div className="font-mono text-title-md font-bold tracking-wider" style={tealColorStyle} dir="ltr">
                  {order.familyCode}
                </div>
                <p className="text-label text-gray-600 mt-1">{t.showThisCode}</p>
              </div>
            )}

            {/* Order Items */}
            <div className="mb-4">
              <p className="text-body-sm font-semibold text-gray-700 mb-2">{t.orderDetails}</p>
              <ul className="space-y-1">
                {(order.menuItems || []).map((item, index) => (
                  <li key={index} className="text-body-sm text-gray-600 flex items-start gap-2">
                    <Check size={16} style={tealColorStyle} className="flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Time Information */}
            <div className="space-y-2 text-body-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={16} />
                <span>
                  {t.orderedAt}{' '}
                  {order.createdTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' at '}
                  {order.createdTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
              {isToday && (
                <div className="flex items-center gap-2 font-semibold" style={tealColorStyle}>
                  <Clock size={16} />
                  <span>{t.pickupAt} {pickupAsap ? t.asSoonAsPossible : pickupTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
          </div>

          {/* Restaurant Contact */}
          <div className="p-6 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-gray-500" />
              <p className="text-body-sm text-gray-700">{order.restaurantAddress}</p>
            </div>
            <div className="flex gap-3">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(mapsAddress(order.restaurantAddress, order.restaurant?.zipcode))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 text-midnight px-4 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                style={tealBgStyle}
              >
                <MapPin size={16} />
                {t.getDirections}
              </a>
              {order.restaurantPhone && (
                <a
                  href={`tel:${order.restaurantPhone}`}
                  className="flex-1 flex items-center justify-center gap-2 border-2 px-4 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  style={{ borderColor: BRAND_COLORS.teal, color: BRAND_COLORS.teal }}
                >
                  <Phone size={16} />
                  {t.callRestaurant}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        <button
          onClick={() => navigateTo('home', 'back')}
          style={tealColorStyle}
          className="mb-4 -ms-2 px-2 min-h-touch font-semibold inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
        >
          {backArrow} {t.browseRestaurants}
        </button>

        <h1 className="text-title-lg font-bold text-gray-900 mb-6">{t.myOrders}</h1>

        {orders.length === 0 && (ordersLoading === null || ordersLoading) ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className={`stagger-${i}`}>
                <SkeletonOrderCard />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md">
            <EmptyState
              illustration={<EmptyCartIllustration />}
              title={t.noActiveOrders}
              message={t.noActiveOrdersMessage}
              actionButton={
                <button
                  onClick={() => navigateTo('home', 'back')}
                  className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
                  style={tealBgStyle}
                >
                  {t.browseRestaurants}
                </button>
              }
            />
          </div>
        ) : (
          <div className="space-y-6">
            {todaysOrders.length > 0 && (
              <div>
                <h2 className="text-body-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">{t.ordersToday}</h2>
                <div className="space-y-4">
                  {todaysOrders.map(renderOrderCard)}
                </div>
              </div>
            )}
            {previousOrders.length > 0 && (
              <div>
                <h2 className="text-body-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">{t.previousOrders}</h2>
                <div className="space-y-4">
                  {previousOrders.map(renderOrderCard)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Home View — rendered inline (not as <HomeView />) so React reconciles it as
  // part of the parent tree rather than remounting on every state change. Remount
  // would reset the horizontal scroll position of the filter rows.
  const renderHomeView = () => (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md p-6 mb-6 border-2 border-transparent hover:border-opacity-20 transition-all" style={tealBorderStyle}>
        <div className="mb-3">
          <p className="text-label font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.yourFamilyCode}</p>
          <div className="font-mono text-title-md font-bold tracking-wider" style={tealColorStyle} dir="ltr">
            {userProfile.familyCode}
          </div>
        </div>
        <div className="flex items-baseline gap-3">
          <div className="text-display font-bold" style={tealColorStyle}>{vouchersRemaining}</div>
          <span className="text-body-sm text-gray-500 font-medium">
            meal{vouchersRemaining !== 1 ? 's' : ''} {userProfile.renewalDate ? `${t.availableUntil} ${userProfile.renewalDate}` : t.availableUntil}
          </span>
        </div>
        {/* Visual meal indicators */}
        <div className="flex gap-1 mt-2">
          {[...Array(Math.min(vouchersRemaining, 10))].map((_, i) => (
            <div
              key={i}
              className="w-8 h-2 rounded-full"
              style={{ backgroundColor: BRAND_COLORS.teal, opacity: 0.7 }}
            />
          ))}
          {vouchersRemaining > 10 && (
            <span className="text-label text-gray-500 ms-2 self-center">+{vouchersRemaining - 10} more</span>
          )}
        </div>
        {(() => {
          // The Airtable lookup currently returns multi-language strings (e.g. both
          // "No Pork" and "无猪肉"). Until the master translation pass replaces this
          // with a single-language source, filter by script to keep only entries
          // matching the user's selected language.
          const filtered = filterDietaryByLanguage(userProfile.dietaryRestrictions, language);
          if (!filtered.length) return null;
          return (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-label font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.yourDietaryRestrictions}</p>
              <div className="text-body-sm text-gray-800 font-medium">
                {filtered.join(', ')}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute start-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            ref={searchInputRef}
            id="restaurant-search"
            name="search"
            type="text"
            placeholder={t.search}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Ensure focus is maintained on mobile
              if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                searchInputRef.current.focus();
              }
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            inputMode="text"
            className="w-full ps-12 pe-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#2AACAD] focus:border-opacity-50 shadow-sm"
          />
        </div>

        {/* Neighborhood Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedNeighborhood('all')}
            className={`px-4 min-h-touch inline-flex items-center rounded-full text-body-sm font-medium whitespace-nowrap transition-all ${
              selectedNeighborhood === 'all'
                ? 'text-midnight shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={selectedNeighborhood === 'all' ? { backgroundColor: BRAND_COLORS.teal } : {}}
          >
            {t.allNeighborhoods}
          </button>
          {uniqueNeighborhoods.map(neighborhood => (
            <button
              key={neighborhood}
              onClick={() => setSelectedNeighborhood(neighborhood)}
              className={`px-4 min-h-touch inline-flex items-center rounded-full text-body-sm font-medium whitespace-nowrap transition-all ${
                selectedNeighborhood === neighborhood
                  ? 'text-midnight shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedNeighborhood === neighborhood ? { backgroundColor: BRAND_COLORS.teal } : {}}
            >
              {neighborhood}
            </button>
          ))}
        </div>

        {/* Cuisine Filter (collapsible — keeps location-first UX) */}
        <div className="mt-3">
          <button
            onClick={() => setCuisineFilterOpen(!cuisineFilterOpen)}
            className="-ms-1 px-1 min-h-touch inline-flex items-center gap-2 text-body-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
          >
            <span>
              Filter by cuisine{selectedCuisine !== 'all' ? `: ${selectedCuisine}` : ''}
            </span>
            <ChevronRight
              size={16}
              className={`transition-transform ${cuisineFilterOpen ? 'rotate-90' : ''}`}
            />
          </button>
          {cuisineFilterOpen && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mt-2">
              <button
                onClick={() => setSelectedCuisine('all')}
                className={`px-4 min-h-touch inline-flex items-center rounded-full text-body-sm font-medium whitespace-nowrap transition-all ${
                  selectedCuisine === 'all'
                    ? 'text-midnight shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={selectedCuisine === 'all' ? { backgroundColor: BRAND_COLORS.teal } : {}}
              >
                {t.allCuisines}
              </button>
              {uniqueCuisines.map(cuisine => (
                <button
                  key={cuisine}
                  onClick={() => setSelectedCuisine(cuisine)}
                  className={`px-4 min-h-touch inline-flex items-center rounded-full text-body-sm font-medium whitespace-nowrap transition-all ${
                    selectedCuisine === cuisine
                      ? 'text-midnight shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={selectedCuisine === cuisine ? { backgroundColor: BRAND_COLORS.teal } : {}}
                >
                  {cuisine}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active-filter bar: shows result count + a one-tap reset, right under the
            filters so it's visible without scrolling — even when filters match zero
            restaurants. Makes it obvious filters are narrowing the list and easy to
            broaden back to all. */}
        {hasActiveFilters && (
          <div className="mt-3 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-body-sm text-gray-600">
              <Store size={14} style={tealColorStyle} />
              {sortedRestaurants.length}
            </span>
            <button
              onClick={() => {
                setSelectedNeighborhood('all');
                setSelectedCuisine('all');
                setSearchQuery('');
                setCuisineFilterOpen(false);
              }}
              className="text-body-sm font-semibold hover:underline"
              style={tealColorStyle}
            >
              {t.clearFilters}
            </button>
          </div>
        )}
      </div>

      <h3 className="text-title-sm font-bold mb-5 text-gray-900">{t.browseRestaurants}</h3>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`stagger-${Math.min(i, 4)}`}>
              <SkeletonRestaurantCard />
            </div>
          ))}
        </div>
      ) : sortedRestaurants.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md">
          <EmptyState
            illustration={<NoRestaurantsIllustration />}
            title={hasActiveFilters ? t.noRestaurantsFiltered : "No Restaurants Available"}
            message={hasActiveFilters ? t.noRestaurantsFilteredMessage : "We're currently updating our restaurant list. Please check back soon for delicious meals from local San Francisco restaurants!"}
            actionButton={
              hasActiveFilters ? (
                <button
                  onClick={() => {
                    setSelectedNeighborhood('all');
                    setSelectedCuisine('all');
                    setSearchQuery('');
                  }}
                  className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
                  style={tealBgStyle}
                >
                  {t.clearFilters}
                </button>
              ) : (
                <button
                  onClick={() => loadDemoData()}
                  className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
                  style={tealBgStyle}
                >
                  Refresh
                </button>
              )
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {sortedRestaurants.map((restaurant, index) => {
            // Browsable whenever the restaurant is in service — families can view the
            // menu even when it's closed (ordering is disabled inside the menu).
            // The OPEN/CLOSED badge stays driven by isOpen.
            const canBrowse = restaurant.inService;
            const isClickable = canBrowse;
            const mealsRemaining = restaurant.dailyMealLimit - restaurant.mealsOrderedToday;
            // Only apply animation classes on first load, not on re-renders
            const staggerClass = !hasAnimated && index < 4 ? `stagger-${index + 1}` : '';
            const fadeInClass = !hasAnimated ? 'fade-in' : '';

            return (
              <div
                key={restaurant.id}
                onClick={() => {
                  if (isClickable) {
                    setSelectedRestaurant(restaurant);
                    setCurrentView('menu');
                  }
                }}
                className={`bg-white rounded-card shadow-elevation-1 overflow-hidden transition-shadow duration-200 ${fadeInClass} ${staggerClass} ${
                  isClickable
                    ? 'cursor-pointer card-interactive hover:shadow-elevation-2'
                    : 'opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start gap-3 p-4">
                  <ShimmerImage
                    src={restaurant.logo}
                    alt={restaurant.dbaName}
                    className={`w-16 h-16 flex-shrink-0 rounded-lg ${!isClickable ? 'opacity-50' : ''}`}
                    fallbackTextClass="text-3xl"
                    fallbackEmoji="🍽️"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 leading-tight mb-1">{restaurant.dbaName}</h4>
                    <span className={`text-label px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 mb-1.5 ${
                      restaurant.isOpen
                        ? 'text-midnight'
                        : 'bg-red-100 text-red-700'
                    }`}
                    style={restaurant.isOpen ? { backgroundColor: BRAND_COLORS.teal } : {}}
                    >
                      {restaurant.isOpen ? (
                        <>
                          <Store size={10} />
                          <span>OPEN</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={10} />
                          <span>CLOSED</span>
                        </>
                      )}
                    </span>
                    <p className="text-label text-gray-500">
                      <span className="font-semibold text-gray-700">Pickup Only</span> · {restaurant.cuisine}
                      {restaurant.neighborhood && ` · ${restaurant.neighborhood}`}
                    </p>
                    <div className="flex items-center gap-1.5 text-label text-gray-400 mt-1">
                      <Clock size={12} />
                      <span>{restaurant.hours}</span>
                    </div>
                  </div>
                  {isClickable && <ChevronRight className="text-gray-400 flex-shrink-0 self-center" size={18} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Menu View — rendered inline (not as <MenuView />) so React reconciles it as
  // part of the parent tree rather than remounting on every cart state change.
  // Remount reset each <img>'s inline opacity:0 and re-triggered the fade-in,
  // which read as a flash when tapping Add to Order. Same pattern as renderCartView.
  const renderMenuView = () => (
    <div className={`max-w-4xl mx-auto ${cart.length > 0 ? 'pb-[calc(12rem+env(safe-area-inset-bottom))]' : 'pb-24'}`}>
      <div className="bg-white shadow-md sticky top-16 z-40">
        <div className="px-4 py-5">
          <button
            onClick={() => navigateTo('home', 'back')}
            style={tealColorStyle}
            className="mb-2 -ms-2 px-2 min-h-touch font-semibold inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
          >
            {backArrow} Back
          </button>
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 relative">
              {selectedRestaurant.logo ? (
                <img
                  src={selectedRestaurant.logo}
                  alt={selectedRestaurant.dbaName}
                  className="w-full h-full object-cover"
                  style={{ opacity: 0, transition: 'opacity 0.2s ease-in' }}
                  onLoad={(e) => {
                    e.target.style.opacity = 1;
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-4xl">🍽️</div>';
                  }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl">
                  🍽️
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-title-md font-bold text-gray-900 leading-tight">{selectedRestaurant.dbaName}</h2>
              <p className="text-gray-600 font-medium mt-1">{selectedRestaurant.cuisine}</p>
              {(selectedRestaurant.neighborhood || selectedRestaurant.address) && (
                <div className="flex items-center gap-1 text-body-sm text-gray-600 mt-1">
                  <MapPin size={14} className="flex-shrink-0" />
                  <span>
                    {selectedRestaurant.neighborhood}
                    {selectedRestaurant.neighborhood && selectedRestaurant.address && ' - '}
                    {selectedRestaurant.address && (
                      <a
                        href={`https://maps.google.com/maps?q=${encodeURIComponent(mapsAddress(selectedRestaurant.address, selectedRestaurant.zipcode))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        style={tealColorStyle}
                      >
                        {selectedRestaurant.address}
                      </a>
                    )}
                  </span>
                </div>
              )}
              {Array.isArray(selectedRestaurant.weeklyHours) && selectedRestaurant.weeklyHours.some(d => d.formatted && d.formatted !== 'Closed') ? (
                (() => {
                  const sfToday = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', weekday: 'long' }).format(new Date());
                  const todayEntry = selectedRestaurant.weeklyHours.find(d => d.day === sfToday);
                  return (
                    <>
                      <details className="mt-1 group">
                        <summary className="flex items-center gap-1 text-body-sm text-gray-600 mt-1 cursor-pointer list-none">
                          <Clock size={14} />
                          <span>{selectedRestaurant.hours}</span>
                          <ChevronRight size={14} className="transition-transform group-open:rotate-90" />
                        </summary>
                        <div className="mt-2 ms-5 space-y-0.5">
                          {selectedRestaurant.weeklyHours.map(d => (
                            <div key={d.day} className={`flex justify-between gap-4 text-body-sm ${d.day === sfToday ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                              <span>{d.day}</span>
                              <span>{d.formatted}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                      {todayEntry?.lastOrder && (
                        <p className="text-label text-gray-500 mt-1">
                          {t.familyMealLastOrder} {todayEntry.lastOrder}
                        </p>
                      )}
                    </>
                  );
                })()
              ) : selectedRestaurant.hours && (
                <div className="flex items-center gap-1 text-body-sm text-gray-600 mt-1">
                  <Clock size={14} />
                  <span>{selectedRestaurant.hours}</span>
                </div>
              )}
              {selectedRestaurant.phone && (
                <a
                  href={`tel:${selectedRestaurant.phone}`}
                  className="flex items-center gap-1 text-body-sm mt-1 hover:underline"
                  style={tealColorStyle}
                >
                  <Phone size={14} />
                  <span>{selectedRestaurant.phone}</span>
                </a>
              )}
              <p className="text-label text-gray-500 mt-1">Pickup only</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-5">
        {!selectedRestaurant.isOpen && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 flex items-start gap-2">
            <XCircle size={18} className="flex-shrink-0 mt-0.5 text-yellow-700" />
            <p className="text-body-sm text-gray-800">{t.closedBanner}</p>
          </div>
        )}
        <h3 className="text-title-sm font-bold text-gray-900">{t.menu}</h3>
        {selectedRestaurant.menu.map(item => (
          <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
            <ShimmerImage
              src={item.imageUrl}
              alt={getMenuItemName(item)}
              className="w-full h-52 rounded-t-xl"
              fallbackTextClass="text-display"
              fallbackEmoji="🍽️"
            />
            <div className="p-5">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 text-body-lg leading-tight">{getMenuItemDescription(item)}</h4>
                  {Array.isArray(item.dietaryConsiderations) && item.dietaryConsiderations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.dietaryConsiderations.map((label, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center bg-gray-100 text-gray-700 text-label px-2 py-0.5 rounded-full font-medium"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 inline-flex items-center text-midnight text-label px-3 py-1.5 rounded-full font-semibold shadow-sm" style={tealBgStyle}>
                    <span>{item.vouchers} meal{item.vouchers > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              {(() => {
                const canOrder = selectedRestaurant.isOpen && vouchersRemaining >= item.vouchers;
                const label = !selectedRestaurant.isOpen
                  ? t.currentlyClosed
                  : (vouchersRemaining < item.vouchers ? t.notEnoughMeals : t.addToCart);
                return (
                  <button
                    onClick={() => addToCart(item, selectedRestaurant)}
                    disabled={!canOrder}
                    className={`w-full h-touch-md mt-4 rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] disabled:cursor-not-allowed ${
                      canOrder
                        ? 'text-midnight hover:opacity-90'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                    style={canOrder ? { backgroundColor: BRAND_COLORS.teal } : {}}
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Cart View
  // Cart View — rendered inline (not as <CartView />) so React reconciles it as
  // part of the parent tree rather than remounting on every state change. Remount
  // re-triggered the EmptyState fade-in animation on every nav, which read as a flicker.
  const renderCartView = () => (
    <div className="max-w-4xl mx-auto pb-[calc(6rem+env(safe-area-inset-bottom))]">
      <div className="bg-white shadow-md sticky top-16 z-40">
        <div className="px-4 py-3">
          <button
            onClick={() => {
              // Go back to the menu of the restaurant being ordered from, not Home.
              const r = cart.length > 0
                ? (restaurants.find(x => x.id === cart[0].restaurantId) || selectedRestaurant)
                : selectedRestaurant;
              if (r) {
                setSelectedRestaurant(r);
                navigateTo('menu', 'back');
              } else {
                navigateTo('home', 'back');
              }
            }}
            style={tealColorStyle}
            className="-ms-2 px-2 min-h-touch font-semibold inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
          >
            {backArrow} Back
          </button>
        </div>
      </div>
      <div className="px-4 py-6">
      <h2 className="text-title-md font-bold mb-1 text-gray-900">{t.yourOrder}</h2>
      {cart.length > 0 && (
        <p className="text-body-lg font-bold mb-2" style={tealColorStyle}>{cart[0].restaurantName}</p>
      )}
      <p className="text-body-sm text-gray-500 mb-6">{t.onlyOneRestaurantAtATime}</p>
      
      {cart.length === 0 && !cartSettled ? (
        // Skeleton during the settle window so the empty state doesn't flash in
        // the gap between navigation and any state propagation. Markup matches
        // the Orders tab so the two transitions look identical.
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className={`stagger-${i}`}>
              <SkeletonOrderCard />
            </div>
          ))}
        </div>
      ) : cart.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md">
          <EmptyState
            illustration={<EmptyCartIllustration />}
            title={t.cartEmpty}
            message="Browse our delicious restaurants and add meals to your cart to feed your whole family!"
            actionButton={
              <button
                onClick={() => navigateTo('home', 'back')}
                className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90"
                style={tealBgStyle}
              >
                {t.browseRestaurants}
              </button>
            }
          />
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            {(() => {
              // Group duplicate meals into one line with a ×N count instead of a
              // separate row per meal. lastIndex lets Remove take one off the line.
              const groups = [];
              const byKey = {};
              cart.forEach((item, index) => {
                const key = item.id ?? getMenuItemName(item);
                if (byKey[key] == null) {
                  byKey[key] = groups.length;
                  groups.push({ item, count: 0, lastIndex: index });
                }
                const g = groups[byKey[key]];
                g.count += 1;
                g.lastIndex = index;
              });
              return groups.map((g, gi) => (
                <div key={gi} className="bg-white rounded-xl shadow-md p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 leading-tight">
                        {getMenuItemName(g.item)}
                        {g.count > 1 && (
                          <span className="ms-2 font-bold" style={tealColorStyle}>×{g.count}</span>
                        )}
                      </h4>
                      {Array.isArray(g.item.dietaryConsiderations) && g.item.dietaryConsiderations.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {g.item.dietaryConsiderations.map((label, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center bg-gray-100 text-gray-700 text-label px-2 py-0.5 rounded-full font-medium"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 inline-flex items-center text-midnight text-label px-3 py-1.5 rounded-full font-semibold shadow-sm" style={tealBgStyle}>
                        <span>{g.count} meal{g.count > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(g.lastIndex)}
                      className="-me-2 px-2 min-h-touch inline-flex items-center text-red-600 text-body-sm font-semibold hover:text-red-700 transition-colors whitespace-nowrap"
                    >
                      {t.remove}
                    </button>
                  </div>
                </div>
              ));
            })()}
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-5 mb-6">
            <div className="flex justify-between mb-3 text-gray-700">
              <span className="font-medium">Meals Used:</span>
              <span className="font-bold text-body-lg">{vouchersInCart}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200">
              <span className="font-medium text-gray-700">Meals Remaining:</span>
              <span className="font-bold text-title-sm" style={tealColorStyle}>{vouchersRemaining}</span>
            </div>
          </div>

          {orderError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-body-sm">
              {orderError}
            </div>
          )}
          <button
            onClick={placeOrder}
            disabled={placingOrder}
            className="w-full h-touch-md text-midnight rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={tealBgStyle}
          >
            {placingOrder ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t.checkout}
              </span>
            ) : t.checkout}
          </button>
        </>
      )}
      </div>
    </div>
  );

  // Bottom-nav item — rendered inline (renderNavItem call, not <NavItem />) so
  // an unstable function reference can't remount the badge on every parent re-render.
  // active=true bumps stroke weight, shows the active-indicator dot, and switches
  // color to teal. badgeCount, when > 0, renders a goji pill with badge-pop animation
  // re-triggered on count change via key={badgeCount}.
  const renderNavItem = ({ view, icon: Icon, label, onClick, badgeCount }) => {
    const active = currentView === view;
    return (
      <button
        onClick={onClick}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        className="relative flex flex-col items-center justify-center flex-1 min-h-touch transition-colors duration-150"
        style={{ color: active ? BRAND_COLORS.teal : '#9CA3AF' }}
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full transition-opacity duration-200"
          style={{ backgroundColor: BRAND_COLORS.teal, opacity: active ? 1 : 0 }}
        />
        <div className="relative mt-1.5">
          <Icon size={24} strokeWidth={active ? 2.5 : 1.8} />
          {badgeCount > 0 && (
            <span
              key={badgeCount}
              className="badge-pop absolute -top-1.5 -end-1.5 min-w-[18px] h-[18px] px-1 text-white text-label rounded-full flex items-center justify-center"
              style={{ backgroundColor: BRAND_COLORS.goji }}
            >
              {badgeCount > 9 ? '9+' : badgeCount}
            </span>
          )}
        </div>
        <span className="text-label mt-1">{label}</span>
      </button>
    );
  };

  // Main render
  if (authLoading) {
    // Show loading screen while checking for existing session
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={tealBorderStyle}></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/sfnd-hsa-combined-logo.png" alt="SF New Deal & HSA" className="mx-auto mb-4" style={{ maxWidth: '70%', height: 'auto' }} />
            <h1 className="text-title-lg font-bold text-gray-800 mb-2">{t.title}</h1>
            <p className="text-gray-600">{t.welcome}</p>
          </div>

          <div className="bg-white rounded-card shadow-elevation-2 p-6 mb-4">
            {!verificationId ? (
              <div>
                <label className="block text-body-sm font-semibold text-gray-700 mb-2">
                  {t.phoneNumber}
                </label>
                <div className="relative mb-4">
                  {/* +1 country-code prefix with a vertical divider. The input's
                      ps-16 leaves room for the prefix; transition-colors gives the
                      teal focus state a smooth fade instead of a jump. */}
                  <div className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none">
                    <span className="text-body-lg text-gray-500">+1</span>
                    <div className="w-px h-5 bg-gray-300 ms-3" />
                  </div>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={t.enterPhone}
                    maxLength={14}
                    autoComplete="tel-national"
                    autoCorrect="off"
                    autoCapitalize="off"
                    className="w-full h-touch-md rounded-btn border-2 border-gray-200 ps-16 pe-4 text-body-lg text-gray-900 focus:outline-none focus:border-teal transition-colors duration-150 placeholder:text-gray-400"
                  />
                </div>
                {authError && (
                  <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-btn text-body-sm">
                    {authError}
                  </div>
                )}
                <button
                  onClick={sendVerificationCode}
                  disabled={sendingCode || !toE164US(phoneNumber)}
                  className="w-full h-touch-md text-midnight rounded-btn font-semibold text-body-lg disabled:opacity-50 disabled:cursor-not-allowed btn-hover transition-all active:scale-[0.98]"
                  style={tealBgStyle}
                >
                  {sendingCode ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {t.sendCode}
                    </span>
                  ) : t.sendCode}
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-body-sm font-semibold text-gray-700 mb-3">
                  {t.verificationCode}
                </label>
                <OTPInput
                  value={verificationCode}
                  onChange={(v) => setVerificationCode(v.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                />
                {authError && (
                  <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-btn text-body-sm">
                    {authError}
                  </div>
                )}
                <button
                  onClick={verifyCode}
                  disabled={verifyingCode || verificationCode.length !== 6}
                  className="w-full h-touch-md mt-4 text-midnight rounded-btn font-semibold text-body-lg mb-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                  style={tealBgStyle}
                >
                  {verifyingCode ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {t.verify}
                    </span>
                  ) : t.verify}
                </button>
                <button
                  onClick={() => {
                    setVerificationId(null);
                    setVerificationCode('');
                    setAuthError('');
                  }}
                  className="w-full min-h-touch text-gray-600 text-body-sm"
                >
                  {backArrow} {t.phoneNumber}
                </button>
              </div>
            )}
          </div>

          {/* reCAPTCHA container (invisible) */}
          <div id="recaptcha-container"></div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-body-sm text-gray-700">
            <p className="font-semibold mb-1">{t.troubleLoggingIn}</p>
            <p>
              {t.needSupport}{' '}
              <a href="tel:+14154801185" className="font-bold hover:underline" style={tealColorStyle}>
                (415) 480-1185
              </a>
              . {t.extensions}
            </p>
          </div>

          <div className="mt-6 text-center">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-gray-600 px-3 min-h-touch rounded border border-gray-300 text-body-sm"
              aria-label="Language"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="zh">中文</option>
              <option value="vi">Tiếng Việt</option>
              <option value="ar">العربية</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-root min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {renderHeader()}
      
      <div key={currentView} className={`view-anim-${viewDirection}`}>
        {currentView === 'home' && renderHomeView()}
        {currentView === 'menu' && renderMenuView()}
        {currentView === 'cart' && renderCartView()}
        {currentView === 'orders' && <OrdersView />}
        {currentView === 'confirmation' && <ConfirmationView />}
        {currentView === 'help' && <HelpView />}
      </div>

      {/* Fixed overlays — the bottom nav, the menu's "View cart" CTA, and the
          Report Bug button — render through a portal into document.body so they
          have no stacking-context relationship with the view-anim wrapper above.
          Otherwise the wrapper's transform-based slide creates a new stacking
          context that briefly re-paints any fixed siblings, which read as the
          bottom nav "flashing off and back on" during view transitions. */}
      {createPortal(
        <>
          {currentView === 'menu' && cart.length > 0 && (
            <button
              onClick={() => {
                setCartSettled(false);
                setCurrentView('cart');
              }}
              className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] start-4 end-4 z-40 flex items-center justify-between px-5 py-3.5 rounded-btn shadow-elevation-3 text-midnight font-semibold transition-all active:scale-[0.98]"
              style={tealBgStyle}
            >
              <div className="flex items-center gap-2">
                <ShoppingCart size={20} />
                <span>{t.viewCart}</span>
              </div>
              <div className="text-body-sm font-medium opacity-90">
                {cart.length} item{cart.length !== 1 ? 's' : ''} · {vouchersInCart} meal{vouchersInCart !== 1 ? 's' : ''}
              </div>
            </button>
          )}

          <div className="bottom-nav fixed bottom-0 start-0 end-0 bg-white border-t border-gray-200 shadow-elevation-3">
            <div className="max-w-4xl mx-auto flex items-stretch px-2 pt-2 pb-3">
              {renderNavItem({
                view: 'home',
                icon: Search,
                label: t.browse,
                onClick: () => setCurrentView('home'),
              })}
              {renderNavItem({
                view: 'cart',
                icon: ShoppingCart,
                label: t.cart,
                onClick: () => {
                  setCartSettled(false);
                  setCurrentView('cart');
                },
                badgeCount: cart.length,
              })}
              {renderNavItem({
                view: 'orders',
                icon: Ticket,
                label: t.orders,
                onClick: () => {
                  setCurrentView('orders');
                  loadOrders();
                },
                badgeCount: orders.length,
              })}
              {renderNavItem({
                view: 'help',
                icon: HelpCircle,
                label: t.help,
                onClick: () => setCurrentView('help'),
              })}
            </div>
          </div>

          <button
            onClick={() => window.open('https://form.asana.com/?k=RiHhVqb0NLaAJgKaDMPhHA&d=1199705537708056', '_blank')}
            className="fixed bottom-20 end-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-midnight text-body-sm font-medium"
            style={{ backgroundColor: BRAND_COLORS.teal }}
            aria-label="Report a bug"
          >
            <MessageSquare size={18} />
            Report Bug
          </button>
        </>,
        document.body
      )}

      <ConfirmModal {...confirmProps} />
    </div>
  );
}